import React, { Component } from "react";
import Dropzone from "react-dropzone";
import shortid from "shortid";
import DropArea from "../DropArea";
import { supportedFileFormats } from "../../constants";
import { checkIfFileSizeIsSupported, checkIfFileTypeIsSupported, getFileType } from "../../util";
import { API_URL } from "../../../../util/constants";
import Raven from "../../../../services/raven";
import PropTypes from "prop-types";
import mixpanel from "../../../../services/mixpanel";
import IS_CLIENT_RENDERED from "../../../../util/isClientRendered";

class Uploader extends Component {
  constructor(props) {
    super(props);
    this.initializeUploader = this.initializeUploader.bind(this);
    this.onDrop = this.onDrop.bind(this);
    this.handleFileChange = this.handleFileChange.bind(this);
  }
  componentWillMount() {
    if (this.props.guid) {
      const { guid, ownerId } = this.props;
      this.initializeUploader({ guid, ownerId });
    }
  }
  componentWillUpdate(nextProps, nextState) {
    //initialize uploader when you recieve the new GUID
    if (nextProps.guid && !this.uploader) {
      const { guid, ownerId, images } = nextProps;

      this.initializeUploader({ guid, ownerId });

      let unUploadedImages = images.filter(img => !img.startedUploading);
      if (!this.props.isEditForm && unUploadedImages.length > 0 && this.uploader) {
        this.uploader.addFiles(unUploadedImages);
      }
    }
  }
  handleFileChange(e) {
    e.preventDefault();
    const acceptedFiles = [];
    const rejectedFiles = { rejectedTypes: [], rejectedSizes: [] };
    const files = [...e.target.files];

    files.forEach(file => {
      if (!checkIfFileTypeIsSupported(file.type)) {
        rejectedFiles.rejectedTypes.push(file);
      } else if (!checkIfFileSizeIsSupported(file.size)) {
        rejectedFiles.rejectedSizes.push(file);
      } else {
        acceptedFiles.push(file);
      }
    });

    const createFileObject = file => {
      let generatedId = shortid.generate();
      return {
        blob: file,
        id: generatedId,
        name: generatedId,
        startedUploading: !!this.uploader,
        type: getFileType(file.type),
      };
    };

    const loadPreview = image =>
      new Promise(resolve => {
        let reader = new FileReader();
        reader.onloadend = () => resolve({ ...image, src: reader.result });
        reader.readAsDataURL(image.blob);
      });

    Promise.all(acceptedFiles.map(createFileObject).map(loadPreview)).then(files => {
      this.props.handleChoosingImages(files);
      if (this.uploader) {
        this.uploader.addFiles(files);
      }
    });

    this.props.setRejectedFilesState(rejectedFiles);
    this.trackFileTypes(files);
  }

  trackFileTypes(files) {
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const fileType = getFileType(file.type);
      if (fileType !== "Unknown") {
        mixpanel.track(`file.select.${fileType}`, { type: file.type, size: file.size / 1024 / 1024 });
      }
    }
  }

  initializeUploader({ guid, ownerId }) {
    let sasUrl = `${API_URL}/api/v1/uploads/sas/${guid}`;
    let successUrl = `${API_URL}/api/v1/uploads/success`;
    const successParams = { guid };

    if (this.props.isAdminEdit) {
      sasUrl = `${API_URL}/api/v1/admin/uploads/sas/${ownerId}/${guid}`;
      successUrl = `${API_URL}/api/v1/admin/uploads/success`;
      successParams.user_id = ownerId;
    }

    if (IS_CLIENT_RENDERED) {
      const qq = require("fine-uploader/lib/azure");
      this.uploader = new qq.azure.FineUploaderBasic({
        signature: {
          endpoint: sasUrl,
          customHeaders: _ => ({
            Authorization: `Bearer ${this.props.accessToken}`,
          }),
        },
        uploadSuccess: {
          endpoint: successUrl,
          params: successParams,
          customHeaders: _ => ({
            Authorization: `Bearer ${this.props.accessToken}`,
          }),
          method: "POST",
        },
        retry: {
          enableAuto: true,
        },
        deleteFile: {
          enabled: true,
        },
        callbacks: {
          onComplete: (id, name, { success }) => {
            if (success) {
              this.props.handleUploadedImage({
                name,
                id,
                uuid: this.uploader.getUuid(id),
              });
              this.props.showImageCaptionTooltip();
            }
          },
          onProgress: (id, name, uploadedBytes, totalBytes) => {
            let percentage = (uploadedBytes / totalBytes) * 100;
            this.props.updateProgressInState(name, percentage);
          },
          onError: (id, name, errorReason, xhrOrXdr) => {
            const formatedErrorMessage = qq.format(
              "Error on file number {} - {}.  Reason: {}. XhrOrXdr: {}",
              id,
              name,
              errorReason,
              xhrOrXdr
            );
            Raven.captureMessage(formatedErrorMessage);
            this.props.handleUploadedImageError(formatedErrorMessage);
          },
        },
      });
      qq.log = (message, level) => {
        if (level === "error") {
          Raven.captureMessage(`fine-uploader error log Message: ${message} - Level: ${level}`);
          this.props.handleUploadedImageError(message);
        } else {
          console.log(message);
        }
      };
    }
  }

  onDrop(acceptedFiles, rejectedFiles) {
    const filesWithinSizeLimit = acceptedFiles.filter(file => checkIfFileSizeIsSupported(file.size));
    const images = filesWithinSizeLimit.map(file => {
      let generatedId = shortid.generate();
      return {
        blob: file,
        id: generatedId,
        name: generatedId,
        src: file.preview,
        startedUploading: !!this.uploader,
        type: getFileType(file.type),
      };
    });

    this.props.handleChoosingImages(images);
    if (this.uploader) {
      this.uploader.addFiles(images);
    }

    const allFiles = [...acceptedFiles, ...rejectedFiles];
    const filesExceedingSizeLimit = allFiles.filter(file => !checkIfFileSizeIsSupported(file.size));

    this.props.setRejectedFilesState({ rejectedTypes: rejectedFiles, rejectedSizes: filesExceedingSizeLimit });
    this.trackFileTypes(allFiles);
  }
  render() {
    const acceptedFormats = supportedFileFormats.join(", ");

    return (
      <div>
        <Dropzone
          id="selectPhotos"
          className="-select-photos-desktop"
          activeClassName="-dropping"
          children={DropArea}
          onDrop={this.onDrop}
          accept={acceptedFormats}
        />
        <div className="-select-photos-mobile">
          <input
            multiple
            className="-inputfile"
            id="newCaseUploader"
            type="file"
            onChange={this.handleFileChange}
            accept={acceptedFormats}
          />
          <label id="newCaseUploader" className="-select-button" htmlFor="newCaseUploader">
            Select Files
          </label>
        </div>
      </div>
    );
  }
}
Uploader.propTypes = {
  guid: PropTypes.string,
  images: PropTypes.array,
  userId: PropTypes.string,
  ownerId: PropTypes.string,
  accessToken: PropTypes.string,
  setRejectedFilesState: PropTypes.func,
  updateProgressInState: PropTypes.func,
  handleChoosingImages: PropTypes.func,
  handleUploadedImage: PropTypes.func,
  handleUploadedImageError: PropTypes.func,
};
export default Uploader;



// WEBPACK FOOTER //
// ./src/scenes/NewCase/components/Uploader/index.js